import React, { createContext, useContext, useCallback, useState } from "react";
import PropTypes from "prop-types";

/**
 * 全站訊息回饋（主計劃：三關審核罐頭訊息、操作成功/失敗提示）。
 *
 * 於 App 根掛 <FeedbackProvider>，任何元件用 useFeedback()：
 *   const fb = useFeedback();
 *   fb.success("儲存成功");
 *   fb.error("儲存失敗，請稍後再試");
 */
const FeedbackContext = createContext(null);

const ICONS = {
  success: { icon: "mdi-check-circle", color: "text-success", title: "成功" },
  error: { icon: "mdi-alert-circle", color: "text-danger", title: "錯誤" },
  warning: { icon: "mdi-alert", color: "text-warning", title: "提醒" },
  info: { icon: "mdi-information", color: "text-info", title: "訊息" },
};

let seq = 1;

export const FeedbackProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const remove = useCallback((id) => {
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (type, message, title) => {
      const id = seq++;
      setToasts((list) => [...list, { id, type, message, title }]);
      setTimeout(() => remove(id), 3500);
    },
    [remove]
  );

  const api = {
    success: (m, t) => push("success", m, t),
    error: (m, t) => push("error", m, t),
    warning: (m, t) => push("warning", m, t),
    info: (m, t) => push("info", m, t),
  };

  return (
    <FeedbackContext.Provider value={api}>
      {children}
      <div style={{ position: "fixed", top: 76, right: 16, zIndex: 1080, width: 320 }}>
        {toasts.map((t) => {
          const meta = ICONS[t.type] || ICONS.info;
          return (
            <div key={t.id} className="card mb-2 shadow-sm border-0">
              <div className="card-body d-flex align-items-start p-2">
                <i className={`mdi ${meta.icon} ${meta.color} me-2`} style={{ fontSize: 20 }} />
                <div className="flex-grow-1">
                  <div className="fw-semibold small">{t.title || meta.title}</div>
                  <div className="text-muted small">{t.message}</div>
                </div>
                <button type="button" className="btn-close btn-sm ms-2" aria-label="關閉" onClick={() => remove(t.id)} />
              </div>
            </div>
          );
        })}
      </div>
    </FeedbackContext.Provider>
  );
};
FeedbackProvider.propTypes = { children: PropTypes.node };

export function useFeedback() {
  const ctx = useContext(FeedbackContext);
  if (!ctx) {
    // 未包 Provider 的安全退路：退回 console，避免崩潰
    /* eslint-disable no-console */
    return {
      success: (m) => console.log("[ok]", m),
      error: (m) => console.error("[err]", m),
      warning: (m) => console.warn("[warn]", m),
      info: (m) => console.info("[info]", m),
    };
    /* eslint-enable no-console */
  }
  return ctx;
}

export default FeedbackProvider;
